/**
 * useOdieInvoiceFeatures - ODIE-specific invoice analysis features.
 *
 * This hook encapsulates all ODIE-specific logic for invoice analysis:
 * - Co-insurance rates
 * - Apply invoice item functionality
 * - Coverage-based entity selection
 * - Finish handler
 *
 * TODO: Remove this hook when ODIE-specific features are no longer needed.
 * All ODIE-specific code is grouped here for easy identification and removal.
 */
import { useMemo } from 'react';

import { useCmsEmbedded } from '~/components/AiChat/hooks/useCmsEmbedded';
import type { CoveredItemRow, StatusType } from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/types';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import useFetchExposures from '../../exposures/useFetchExposures';
import { useCms } from '../../hooks/useCms';
import { useCoverageConfigs } from '../../hooks/useCoverageConfigs';

type Exposure = NonNullable<ClaimPolymorph['exposures']>[number];

interface UseOdieInvoiceFeaturesParams {
  documentId: number;
  onClose: () => void;
  setIsSubmitting: (value: boolean) => void;
}

interface UseOdieInvoiceFeaturesReturn {
  isOdieMode: boolean;
  coInsuranceRates: Record<string, number>;
  coverages: Array<{ value: string; description: string }>;
  isCoverageConfigsLoading: boolean;
  isCoverageConfigsError: boolean;
  exposuresByCoverageKey: Record<string, Exposure>;
  handleFinish: (() => Promise<boolean>) | undefined;
  handleApplyCoveredItem:
    | ((item: CoveredItemRow, onUpdate: (data: { message?: string; status: StatusType }) => void) => Promise<void>)
    | undefined;
}

export const useOdieInvoiceFeatures = ({
  documentId,
  onClose,
  setIsSubmitting,
}: UseOdieInvoiceFeaturesParams): UseOdieInvoiceFeaturesReturn => {
  const { userOrganization } = useCms();
  const { applyInvoiceItem, coInsuranceRates } = useCmsEmbedded();
  const { exposures } = useFetchExposures();
  const { coverageConfigs, isCoverageConfigsLoading, isCoverageConfigsError } = useCoverageConfigs();

  // ODIE mode is enabled when the FF is on (org-wide setting)
  const isOdieMode = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.AI_INVOICE_ODIE_SPECIFIC_CALCULATION
  );

  // Coverage options from org-level coverage configs
  const coverages = useMemo(
    () =>
      coverageConfigs
        .filter((c) => !c.is_disabled)
        .map((c) => ({
          value: c.coverage_key,
          description: c.display_name,
        })),
    [coverageConfigs]
  );

  // Map exposures by coverage key for ODIE mode
  const exposuresByCoverageKey = useMemo(
    (): Record<string, Exposure> =>
      Object.fromEntries((exposures || []).map((exposure) => [exposure.coverage_type, exposure])),
    [exposures]
  );

  // Handle finish action (ODIE mode only)
  const handleFinish = isOdieMode
    ? (): Promise<boolean> => {
        onClose();
        return Promise.resolve(true);
      }
    : undefined;

  // Handle apply covered item (ODIE mode only)
  const handleApplyCoveredItem = isOdieMode
    ? async (item: CoveredItemRow, onUpdate: (data: { message?: string; status: StatusType }) => void) => {
        try {
          setIsSubmitting(true);
          await applyInvoiceItem({
            item,
            documentId,
            onUpdate,
          });
        } finally {
          setIsSubmitting(false);
        }
      }
    : undefined;

  return {
    isOdieMode,
    coInsuranceRates: isOdieMode ? coInsuranceRates : {},
    coverages,
    isCoverageConfigsLoading,
    isCoverageConfigsError,
    exposuresByCoverageKey,
    handleFinish,
    handleApplyCoveredItem,
  };
};

export default useOdieInvoiceFeatures;
